<template>
  <div v-if="model.uuid" class="about">
    <page-header :title="model.name">
      <li class="breadcrumb-item">
        <router-link :to="{name:'applicationWindow.index'}">{{ $tc('applicationWindow.name', 2) }}</router-link>
      </li>
      <li class="breadcrumb-item">
        <router-link :to="{name:'applicationWindow.show', params: {applicationWindow: model.applicationWindow.uuid}}">
          {{ model.applicationWindow.name }}
        </router-link>
      </li>
      <li class="breadcrumb-item">{{ model.teamName }}</li>
    </page-header>
    <page-content>
      <div class="row">
        <div class="offset-md-0 col-md-12 offset-lg-1 col-lg-10 offset-xl-2 col-xl-8">
          <div v-if="isTeam" class="bg-image">
            <div style="background-color: #0B2C36">
              <div class="content content-full text-center">
                <div class="my-3">
                  <img v-if="model.logo" class="img-avatar img-avatar-thumb bg-white" :src="model.logo" alt="">
                  <img v-else class="img-avatar img-avatar-thumb bg-white" src="@/assets/le.png" alt="">
                </div>
                <h1 class="h2 text-white mb-0">{{ model.teamName }}</h1>
              </div>
            </div>
          </div>
          <div class="bg-white border-bottom">
            <div class="content content-boxed py-3">
              <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-items-center flex-wrap" style="gap: 16px;">
                  <span class="fs-sm fw-semibold text-muted">#{{ model.applicationNumber }}</span>
                </div>
                <div class="d-flex align-items-center flex-wrap" style="gap: 16px;">
                  <span class="badge px-2 py-1 text-uppercase fs-sm"
                        :class="model.type === 'TEAM' ? 'badge-primary' : 'badge-info'">
                    {{ $t(`select.options.${model.type}`) }}
                  </span>
                  <div class="d-flex align-items-center" style="gap: 10px;">
                    <span class="fs-sm fw-semibold text-muted text-uppercase">{{ $t('status') }}:</span>
                    <span class="badge px-2 py-1 text-uppercase fs-sm"
                          :class="{'badge-warning': model.status === 'PENDING', 'badge-success': model.status === 'APPROVED', 'badge-danger': model.status === 'REJECTED'}">
                      {{ $t(`statusBadge.${model.status.toLowerCase()}`) }}
                    </span>
                  </div>
                </div>
                <div class="d-flex" style="gap: 8px;">
                  <template v-if="model.status === 'PENDING'">
                    <button class="btn btn-success" @click="updateStatus('APPROVED')">
                      <i class="fa fa-check mr-1"></i> {{ $t('statusBadge.approve') }}
                    </button>
                    <button class="btn btn-danger" @click="updateStatus('REJECTED')">
                      <i class="fa fa-times mr-1"></i> {{ $t('statusBadge.reject') }}
                    </button>
                  </template>
                  <template v-else-if="model.status === 'APPROVED'">
                    <button class="btn btn-danger" @click="updateStatus('REJECTED')">
                      <i class="fa fa-times mr-1"></i> {{ $t('statusBadge.reject') }}
                    </button>
                    <button class="btn btn-warning" @click="updateStatus('PENDING')">
                      <i class="fa fa-undo mr-1"></i> {{ $t('statusBadge.pending') }}
                    </button>
                  </template>
                  <template v-else-if="model.status === 'REJECTED'">
                    <button class="btn btn-success" @click="updateStatus('APPROVED')">
                      <i class="fa fa-check mr-1"></i> {{ $t('statusBadge.approve') }}
                    </button>
                    <button class="btn btn-warning" @click="updateStatus('PENDING')">
                      <i class="fa fa-undo mr-1"></i> {{ $t('statusBadge.pending') }}
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white border-bottom">
            <div class="content content-boxed">
              <div class="row items-push text-center">
                <div class="col-6 col-md-4">
                  <div class="cursor-pointer rounded border py-2" @click="openCategoryModal()">
                    <div class="fs-sm fw-semibold text-muted text-uppercase">{{ $tc('category.name', 1) }}</div>
                    <a type="button"
                       href="javascript:void(0)">{{ model.category }}</a>
                  </div>
                </div>
                <div v-if="isTeam" class="col-6 col-md-4">
                  <div class="fs-sm fw-semibold text-muted text-uppercase">{{ $tc('coach.name', 2) }}</div>
                  <a class="link-fx fs-3" href="javascript:void(0)" @click="scrollTo('section-coaches')">{{ model.coaches.length }}</a>
                </div>
                <div class="col-6 col-md-4">
                  <div class="fs-sm fw-semibold text-muted text-uppercase">{{ $tc('player.name', 2) }}</div>
                  <a class="link-fx fs-3" href="javascript:void(0)" @click="scrollTo('section-players')">{{ model.players.length }}</a>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-white border-bottom">
            <h3 class="h3 text-center m-0 pt-3">{{ $t('manager') }}</h3>
            <div class="content content-boxed">
              <div class="row items-push text-center">
                <div class="col-12 col-md-4">
                  <div class="fs-sm fw-semibold text-muted text-uppercase">{{ $t('formly.fields.name') }}</div>
                  <span>{{ model.managerName }}</span>
                </div>
                <div class="col-12 col-md-4">
                  <div class="fs-sm fw-semibold text-muted text-uppercase">{{ $t('formly.fields.phone') }}</div>
                  <a class="link-fx fs-3" :href="`tel:${model.managerPhoneNumber}`">{{ model.managerPhoneNumber }}</a>
                </div>
                <div class="col-12 col-md-4">
                  <div class="fs-sm fw-semibold text-muted text-uppercase">{{ $t('formly.fields.email') }}</div>
                  <a class="link-fx fs-3" :href="`mailto:${model.managerEmail}`">{{ model.managerEmail }}</a>
                </div>
              </div>
            </div>
          </div>
          <div v-if="model.additionalNote" class="bg-white border-bottom">
            <h3 class="h3 text-center m-0 pt-3">{{ $t('remark') }}</h3>
            <div class="content content-boxed">
              <p v-text="model.additionalNote"></p>
            </div>
          </div>
          <div v-if="relevantCustomFields.length > 0" class="bg-white border-bottom">
            <h3 class="h3 text-center m-0 pt-3">{{ $t('customField.title') }}</h3>
            <div class="content content-boxed">
              <div v-for="field in relevantCustomFields" :key="field.uuid" class="row mb-2">
                <div class="col-md-4 col-sm-5 fw-semibold text-muted fs-sm text-uppercase">{{ field.label }}</div>
                <div class="col-md-8 col-sm-7 fs-sm">{{ formatCustomFieldValue(field) }}</div>
              </div>
            </div>
          </div>
          <div v-if="model.rejections && model.rejections.length > 0" class="bg-white border-bottom">
            <h3 class="h3 text-center m-0 pt-3">{{ $t('team_application.rejectionHistory') }}</h3>
            <div class="content content-boxed">
              <div v-for="(rejection, index) in model.rejections" :key="index" class="mb-3 p-3 border rounded" :class="rejection.isResolved ? 'border-success' : 'border-danger'">
                <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap: 8px;">
                  <div class="d-flex align-items-center" style="gap: 6px;">
                    <span class="badge text-uppercase" :class="rejection.isResolved ? 'badge-info' : 'badge-danger'">
                      {{ rejection.isResolved ? $t('statusBadge.resolved') : $t('statusBadge.rejected') }}
                    </span>
                    <span class="text-muted fs-sm">{{ rejection.createdAt }}</span>
                  </div>
                  <button
                    v-if="model.status !== 'REJECTED'"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="rejectWithSameReason(rejection.reason)"
                  >
                    <i class="fa fa-times mr-1"></i> {{ $t('statusBadge.rejectAgain') }}
                  </button>
                </div>
                <p class="mb-0 fs-sm" style="white-space: pre-wrap;">{{ rejection.reason }}</p>
              </div>
            </div>
          </div>
          <ntm-block>
            <div v-if="isTeam" class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h3 id="section-coaches" class="h3 mb-0">{{ $tc('coach.name', 2) }}</h3>
                <div>
                  <button class="btn btn-sm btn-success" @click="addNewCoach()">{{ $t('action.addNew') }}</button>
                </div>
              </div>
              <div v-if="model.coaches.length > 0" class="table-responsive">

                <table class="table table-bordered table-striped table-vcenter">
                  <thead>
                  <tr>
                    <th>{{ $t('table.column.name') }}</th>
                    <th class="text-center">{{ $t('table.column.yearOfBirth') }}</th>
                    <th class="text-center" style="width: 100px;">{{ $t('table.column.actions') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="coach in model.coaches" :key="coach.uuid">
                    <td class="fw-semibold fs-sm">
                      <a href="javascript:void(0)">{{ coach.firstName }} {{ coach.lastName }}</a>
                    </td>
                    <td class="text-center">{{ birthYear(coach.dateOfBirth) }}</td>
                    <td class="text-center">
                      <div class="btn-group">
                        <button type="button" @click="openUpdateCoachModal(coach)" class="btn btn-sm btn-info" v-tooltip="$t('action.edit')">
                          <i class="fa fa-pencil"></i>
                        </button>
                        <button type="button" @click="deleteCoach(coach)" class="btn btn-sm btn-danger" v-tooltip="$t('action.delete')">
                          <i class="fa fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h3 id="section-players" class="h3 mb-0">{{ $tc('player.name', 2) }}</h3>
                <div>
                  <button class="btn btn-sm btn-success" @click="addNewPlayer()">{{ $t('action.addNew') }}</button>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-bordered table-striped table-vcenter">
                  <thead>
                  <tr>
                    <!--                <th class="text-center" style="width: 100px;">-->
                    <!--                  <i class="fa fa-user"></i>-->
                    <!--                </th>-->
                    <th>{{ $t('table.column.name') }}</th>
                    <th class="text-center">{{
                        $t(model.applicationWindow.hasFullDateOfBirth ? 'table.column.dateOfBirth' : 'table.column.yearOfBirth')
                      }}
                    </th>
                    <th class="text-center" v-if="model.applicationWindow.playerIdentificationNumberInputLabel">
                      {{ model.applicationWindow.playerIdentificationNumberInputLabel }}
                    </th>
                    <th class="text-center" v-if="showPositionColumn">{{ $t('table.column.position') }}</th>
                    <th v-if="showTagsColumn">{{ $tc('playerTag.name', 2) }}</th>
                    <th class="text-center" style="width: 100px;">{{ $t('table.column.actions') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="player in model.players" :key="player.uuid">
                    <!--                <td class="text-center">-->
                    <!--                  <img class="img-avatar img-avatar32" src="@/assets/media/avatars/avatar1.jpg" alt="">-->
                    <!--                </td>-->
                    <td class="fw-semibold fs-sm">
                      <a href="javascript:void(0)">{{ player.firstName }} {{ player.lastName }}</a>
                    </td>
                    <td class="text-center">
                      {{
                        model.applicationWindow.hasFullDateOfBirth
                          ? $d(parseDateOnly(player.dateOfBirth), 'short')
                          : birthYear(player.dateOfBirth)
                      }}
                    </td>
                    <td class="text-center" v-if="model.applicationWindow.playerIdentificationNumberInputLabel">
                  <span class="fs-xs fw-semibold d-inline-block py-1 px-3 rounded-pill bg-info-light text-info">{{
                      player.identificationNumber
                    }}</span>
                    </td>
                    <td class="text-center" v-if="showPositionColumn">{{ player.position || '—' }}</td>
                    <td v-if="showTagsColumn">
                      <span v-for="tag in (player.playerTags || [])" :key="tag.uuid" class="badge mr-1" :style="tagStyle(tag)">{{ tag.name }}</span>
                    </td>
                    <td class="text-center">
                      <div class="btn-group">
                        <button type="button" @click="openUpdatePlayerModal(player)" class="btn btn-sm btn-info" v-tooltip="$t('action.edit')">
                          <i class="fa fa-pencil"></i>
                        </button>
                        <button type="button" @click="deletePlayer(player)" class="btn btn-sm btn-danger" v-tooltip="$t('action.delete')">
                          <i class="fa fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </ntm-block>
        </div>
      </div>
    </page-content>
    <modal name="reject-application" :adaptive="true" height="auto">
      <ntm-block :title="$t('swal.rejectApplication.title', { entity: model.teamName })" :collapsable="false">
        <template #options>
          <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('reject-application')" v-tooltip="$t('close')">
            <i class="fa fa-times"></i>
          </button>
        </template>
        <div class="form-group mb-3">
          <label class="fw-semibold">{{ $t('team_application.rejectionReason') }}</label>
          <textarea class="form-control mt-1" v-model="rejectModal.reason" rows="5" :placeholder="$t('team_application.rejectionReason')"></textarea>
        </div>
        <div class="d-flex justify-content-center mb-3" style="gap: 8px;">
          <button type="button" class="btn btn-light" @click="$modal.hide('reject-application')">{{ $t('team_application.cancel') }}</button>
          <button type="button" class="btn btn-danger" @click="submitReject()" :disabled="rejectModal.loading || !rejectModal.reason.trim()">
            <span v-if="!rejectModal.loading">{{ $t('statusBadge.reject') }}</span>
            <i v-if="rejectModal.loading" class="fa fa-cog fa-spin"></i>
          </button>
        </div>
      </ntm-block>
    </modal>
    <modal name="update-category" :adaptive="true" height="auto">
      <ntm-block :title="$t('application.updateCategory')" :collapsable="false">
        <template #options>
          <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('update-category')" v-tooltip="$t('close')">
            <i class="fa fa-times"></i>
          </button>
        </template>
        <div class="d-flex justify-content-center align-items-center mb-3" style="gap: 5px">
          <button class="btn" v-for="category in model.applicationWindow.categories" @click="updateCategory(category)"
                  :class="{'btn-success': model.category === category}" :key="category">{{ category }}
          </button>
        </div>
      </ntm-block>
    </modal>
    <modal name="update-coach" :adaptive="true" height="auto">
      <form @submit.prevent="submitUpdateCoach()">
        <ntm-block :title="$t('coach.edit')" :collapsable="false">
          <template #options>
            <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('update-coach')" v-tooltip="$t('close')">
              <i class="fa fa-times"></i>
            </button>
          </template>
          <formly-form :form="updateCoach.form" :model="updateCoach.model" :fields="coachFormFields"></formly-form>
          <div class="d-flex mb-2 justify-content-center">
            <button class="btn btn-success push-15 mr-2" :disabled="updateCoach.loading"><span
              v-if="!updateCoach.loading">{{ $t('save') }}</span><i v-if="updateCoach.loading"
                                                                    class="fa fa-cog fa-spin"></i></button>
          </div>
        </ntm-block>
      </form>
    </modal>
    <modal name="update-player" :adaptive="true" height="auto">
      <form @submit.prevent="submitUpdatePlayer()">
        <ntm-block :title="$t('player.edit')" :collapsable="false">
          <template #options>
            <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('update-player')" v-tooltip="$t('close')">
              <i class="fa fa-times"></i>
            </button>
          </template>
          <formly-form :form="updatePlayer.form" :model="updatePlayer.model" :fields="playerFormFields"></formly-form>
          <div v-if="model.applicationWindow.collectPlayerPositions" class="form-group">
            <label class="mb-1">{{ $t('table.column.position') }}</label>
            <select v-model="updatePlayer.model.position" class="form-control">
              <option :value="null">—</option>
              <option v-for="position in model.applicationWindow.playerPositions" :key="position" :value="position">{{ position }}</option>
            </select>
            <span v-if="playerFieldError('position')" class="help-block form-text text-danger">{{ playerFieldError('position') }}</span>
          </div>
          <div v-if="model.applicationWindow.collectPlayerTags" class="form-group">
            <label class="mb-1">{{ $tc('playerTag.name', 2) }}</label>
            <multiselect
              v-model="updatePlayerTags"
              :options="model.applicationWindow.playerTags"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :max="3"
              label="name"
              track-by="uuid"
              :placeholder="$tc('playerTag.name', 2)"
            >
              <template slot="tag" slot-scope="{ option, remove }">
                <span class="multiselect__tag" :style="tagStyle(option)">
                  <span>{{ option.name }}</span>
                  <i class="multiselect__tag-icon" @click.prevent="remove(option)"></i>
                </span>
              </template>
            </multiselect>
            <small v-if="updatePlayerTags.length >= 3" class="text-danger d-block">{{ $t('playerTag.maxTagsReached') }}</small>
            <span v-if="playerFieldError('playerTagUuids')" class="help-block form-text text-danger">{{ playerFieldError('playerTagUuids') }}</span>
          </div>
          <div class="d-flex mb-2 justify-content-center">
            <button class="btn btn-success push-15 mr-2" :disabled="updatePlayer.loading"><span
              v-if="!updatePlayer.loading">{{ $t('save') }}</span><i v-if="updatePlayer.loading"
                                                                     class="fa fa-cog fa-spin"></i></button>
          </div>
        </ntm-block>
      </form>
    </modal>
  </div>
</template>

<script>
import ApplicationWindowService from '@/services/applicationWindow.service'
import Swal from 'sweetalert2'
import VueI18n from '@/i18n'
import { useTeamApplications } from '@/use/useTeamApplications'
import Multiselect from 'vue-multiselect'

export default {
  components: { Multiselect },
  setup () {
    const { fetchPendingTeamApplicationsCount } = useTeamApplications()
    return { fetchPendingTeamApplicationsCount }
  },
  data () {
    return {
      updatePlayer: {
        form: {},
        model: {},
        loading: false
      },
      updateCoach: {
        form: {},
        model: {},
        loading: false
      },
      rejectModal: {
        reason: '',
        loading: false
      },
      model: {},
      loading: false
    }
  },
  computed: {
    isTeam () {
      return this.model.type === 'TEAM'
    },
    relevantCustomFields () {
      const type = this.model.type
      return (this.model.applicationWindow?.customFields ?? []).filter(f => f.applicationType === type)
    },
    coachFormFields () {
      return [
        {
          key: 'firstName',
          type: 'input',
          templateOptions: {
            label: 'firstName'
          }
        },
        {
          key: 'lastName',
          type: 'input',
          templateOptions: {
            label: 'lastName'
          }
        },
        {
          key: 'dateOfBirth',
          type: this.model.applicationWindow.hasFullDateOfBirth ? 'datepicker' : 'input',
          templateOptions: {
            mode: 'date',
            label: this.model.applicationWindow.hasFullDateOfBirth ? 'dateOfBirth' : 'birthYear'
          }
        }
      ]
    },
    showPositionColumn () {
      return Boolean(this.model.applicationWindow.collectPlayerPositions) || this.model.players.some(player => player.position)
    },
    showTagsColumn () {
      return Boolean(this.model.applicationWindow.collectPlayerTags) || this.model.players.some(player => (player.playerTags || []).length > 0)
    },
    updatePlayerTags: {
      get () {
        return (this.model.applicationWindow.playerTags || []).filter(tag => (this.updatePlayer.model.playerTagUuids || []).includes(tag.uuid))
      },
      set (tags) {
        this.$set(this.updatePlayer.model, 'playerTagUuids', tags.map(tag => tag.uuid))
      }
    },
    playerFormFields () {
      return [
        {
          key: 'firstName',
          type: 'input',
          templateOptions: {
            label: 'firstName'
          }
        },
        {
          key: 'lastName',
          type: 'input',
          templateOptions: {
            label: 'lastName'
          }
        },
        {
          key: 'dateOfBirth',
          type: this.model.applicationWindow.hasFullDateOfBirth ? 'datepicker' : 'input',
          templateOptions: {
            mode: 'date',
            label: this.model.applicationWindow.hasFullDateOfBirth ? 'dateOfBirth' : 'birthYear'
          }
        },
        {
          key: 'identificationNumber',
          type: 'input',
          templateOptions: {
            translated: false,
            label: this.model.applicationWindow.playerIdentificationNumberInputLabel
          },
          display: () => Boolean(this.model.applicationWindow.playerIdentificationNumberInputLabel)
        }
      ]
    }
  },
  beforeRouteEnter (to, from, next) {
    ApplicationWindowService.teamApplication(to.params.applicationWindow, to.params.teamApplication).then((response) => {
      next(vm => vm.setData(response.data))
    })
  },
  methods: {
    tagStyle (tag) {
      return tag && tag.color ? { backgroundColor: tag.color, color: '#fff' } : {}
    },
    playerFieldError (field) {
      const errors = this.updatePlayer.form.$errors
      if (!errors) return null
      const key = Object.keys(errors).find(k => k === field || k.startsWith(field + '.'))
      const first = key ? [].concat(errors[key])[0] : null
      if (typeof first !== 'string') return null
      if (first.includes(' ')) return first
      return this.$t('validation.' + field + '.' + first.split('.').pop().split('|')[0])
    },
    formatCustomFieldValue (field) {
      const value = (this.model.customFieldValues ?? {})[field.uuid]
      if (field.fieldType === 'boolean' || field.fieldType === 'confirmation') {
        return value ? this.$t('team_application.yes') : this.$t('team_application.no')
      }
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'N/A'
      }
      return (value !== null && value !== undefined && value !== '') ? String(value) : 'N/A'
    },
    scrollTo (id) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    addNewCoach () {
      this.updateCoach.form.$errors = []
      this.updateCoach.model = {
        firstName: '',
        lastName: '',
        dateOfBirth: null
      }
      this.$modal.show('update-coach')
    },
    addNewPlayer () {
      this.updatePlayer.form.$errors = []
      this.updatePlayer.model = {
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        identificationNumber: '',
        position: null,
        playerTagUuids: []
      }
      this.$modal.show('update-player')
    },
    updateCategory (category) {
      this.model.category = category
      this.$modal.hide('update-category')
      ApplicationWindowService.updateTeamApplicationCategory(this.$route.params.applicationWindow, this.$route.params.teamApplication, category)
    },
    updateStatus (status) {
      if (status === 'PENDING') {
        this.model.status = status
        ApplicationWindowService.updateTeamApplicationStatus(this.$route.params.applicationWindow, this.$route.params.teamApplication, status)
          .then(() => this.fetchPendingTeamApplicationsCount())
        return
      }

      if (status === 'REJECTED') {
        this.rejectModal.reason = ''
        this.rejectModal.loading = false
        this.$modal.show('reject-application')
        return
      }

      return Swal.fire({
        title: VueI18n.t('swal.approveApplication.title', { entity: this.model.teamName }),
        text: VueI18n.t('swal.approveApplication.text'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: VueI18n.t('swal.approveApplication.yes'),
        cancelButtonText: VueI18n.t('swal.approveApplication.no'),
        showCloseButton: true,
        showLoaderOnConfirm: true
      }).then((result) => {
        if (result.value) {
          this.model.status = status
          ApplicationWindowService.updateTeamApplicationStatus(this.$route.params.applicationWindow, this.$route.params.teamApplication, status)
            .then(() => this.fetchPendingTeamApplicationsCount())
        }
      })
    },
    rejectWithSameReason (reason) {
      this.rejectModal.reason = reason
      this.rejectModal.loading = false
      this.$modal.show('reject-application')
    },
    submitReject () {
      this.rejectModal.loading = true
      ApplicationWindowService.updateTeamApplicationStatus(
        this.$route.params.applicationWindow,
        this.$route.params.teamApplication,
        'REJECTED',
        this.rejectModal.reason
      ).then(() => {
        this.$modal.hide('reject-application')
        this.fetchPendingTeamApplicationsCount()
        this.fetchData()
      }).finally(() => {
        this.rejectModal.loading = false
      })
    },
    openCategoryModal () {
      this.$modal.show('update-category')
    },
    fetchData () {
      this.loading = true
      ApplicationWindowService.teamApplication(this.$route.params.applicationWindow, this.$route.params.teamApplication).then((response) => {
        this.setData(response.data)
      }).finally(() => {
        this.loading = false
      })
    },
    setData (data) {
      this.model = data
    },
    // The API returns date-only values (YYYY-MM-DD...). Parsing them with `new Date()` treats them as UTC
    // and shifts them back a day (and a year for Jan 1st) in timezones behind UTC, so read the parts directly.
    parseDateOnly (value) {
      const [year, month, day] = String(value).substring(0, 10).split('-').map(Number)
      return new Date(year, month - 1, day)
    },
    birthYear (value) {
      return this.parseDateOnly(value).getFullYear()
    },
    openUpdateCoachModal (coach) {
      this.updateCoach.form.$errors = []
      this.updateCoach.model = { ...coach }
      this.updateCoach.model.dateOfBirth = this.birthYear(this.updateCoach.model.dateOfBirth)

      this.$modal.show('update-coach')
    },
    openUpdatePlayerModal (player) {
      this.updatePlayer.form.$errors = []
      this.updatePlayer.model = { ...player, playerTagUuids: (player.playerTags || []).map(tag => tag.uuid) }
      if (!this.model.applicationWindow.hasFullDateOfBirth) {
        this.updatePlayer.model.dateOfBirth = this.birthYear(this.updatePlayer.model.dateOfBirth)
      }

      this.$modal.show('update-player')
    },
    submitUpdateCoach () {
      this.updateCoach.loading = true
      const dateOfBirth = new Date(`${this.updateCoach.model.dateOfBirth}-01-01`).toISOString()

      ApplicationWindowService.upsertTeamApplicationCoach(this.$route.params.applicationWindow, this.$route.params.teamApplication, {
        id: this.updateCoach.model.id,
        firstName: this.updateCoach.model.firstName,
        lastName: this.updateCoach.model.lastName,
        dateOfBirth: dateOfBirth
      }).then(() => {
        this.fetchData()
        this.$modal.hide('update-coach')
      }).catch((err) => {
        this.form.$errors = err.response.data.errors
      }).finally(() => {
        this.updateCoach.loading = false
      })
    },
    submitUpdatePlayer () {
      this.updatePlayer.loading = true
      let dateOfBirth = this.updatePlayer.model.dateOfBirth

      if (!this.model.applicationWindow.hasFullDateOfBirth) {
        dateOfBirth = new Date(`${this.updatePlayer.model.dateOfBirth}-01-01`).toISOString()
      }

      ApplicationWindowService.upsertTeamApplicationPlayer(this.$route.params.applicationWindow, this.$route.params.teamApplication, {
        id: this.updatePlayer.model.id,
        firstName: this.updatePlayer.model.firstName,
        lastName: this.updatePlayer.model.lastName,
        dateOfBirth: dateOfBirth,
        identificationNumber: this.updatePlayer.model.identificationNumber,
        position: this.updatePlayer.model.position || null,
        playerTagUuids: this.updatePlayer.model.playerTagUuids || []
      }).then(() => {
        this.fetchData()
        this.$modal.hide('update-player')
      }).catch((err) => {
        this.form.$errors = err.response.data.errors
        this.updatePlayer.form.$errors = err.response.data.errors
      }).finally(() => {
        this.updatePlayer.loading = false
      })
    },
    deletePlayer (player) {
      ApplicationWindowService.deleteTeamApplicationPlayer(this.$route.params.applicationWindow, this.$route.params.teamApplication, player.id).then(() => {
        this.fetchData()
      }).catch(() => {
      })
    },
    deleteCoach (coach) {
      ApplicationWindowService.deleteTeamApplicationCoach(this.$route.params.applicationWindow, this.$route.params.teamApplication, coach.id).then(() => {
        this.fetchData()
      }).catch(() => {
      })
    }
  }
}
</script>
